import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import { FaPlay, FaStop, FaCode, FaDownload, FaCheckCircle, FaExclamationTriangle, FaClock } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config';

const WorkflowTester = ({ workflow, workflowType, onClose }) => {
  const [testInputs, setTestInputs] = useState([]);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [testConfiguration, setTestConfiguration] = useState({
    batchSize: 1,
    timeout: 60,
    retries: 1,
    validateOutputs: true,
    logLevel: 'INFO'
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [realTimeResults, setRealTimeResults] = useState([]);
  const [overallStats, setOverallStats] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    running: 0,
    avgExecutionTime: 0
  });
  
  // File input state
  const [inputType, setInputType] = useState('text'); // 'text' or 'file'
  const [availableFiles, setAvailableFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [testFileUploads, setTestFileUploads] = useState({}); // Per-test file uploads

  // Initialize with default test cases
  useEffect(() => {
    const defaultTests = generateDefaultTestCases();
    setTestInputs(defaultTests);
    loadAvailableFiles();
  }, [workflow, workflowType]);
  
  const loadAvailableFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflow-files`);
      const data = response.data;
      setAvailableFiles(data.files || (Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Failed to load files:', error);
    }
  };
  
  const uploadFile = async (file) => {
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `Workflow input for ${workflow.name || 'workflow'}`);
      
      await axios.post(`${API_URL}/api/workflow-files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await loadAvailableFiles();
      alert(`✅ Uploaded ${file.name}`);
    } catch (error) {
      alert(`❌ Failed to upload: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const generateDefaultTestCases = () => {
    if (workflowType === 'automatic') {
      return [
        {
          name: "Basic Test",
          input: workflow.combinedInput || "Test input for automatic workflow",
          expectedOutput: null,
          description: "Basic functionality test"
        },
        {
          name: "Edge Case - Empty Input",
          input: "",
          expectedOutput: null,
          description: "Test behavior with empty input"
        },
        {
          name: "Complex Design Request",
          input: "Create a heart-shaped tactile graphic with braille labels for accessibility, using laser cutting",
          expectedOutput: null,
          description: "Complex design request test"
        }
      ];
    } else {
      // Manual workflow tests
      return [
        {
          name: "Sequential Execution",
          input: {
            "input": "test data for workflow",
            "stage": "initial"
          },
          expectedOutput: null,
          description: "Test sequential agent execution"
        },
        {
          name: "Error Handling",
          input: {
            "input": "invalid data format",
            "stage": "error_test"
          },
          expectedOutput: null,
          description: "Test error handling and recovery"
        },
        {
          name: "Large Input",
          input: {
            "input": "x".repeat(1000),
            "stage": "stress_test"
          },
          expectedOutput: null,
          description: "Stress test with large input"
        }
      ];
    }
  };

  const addTestCase = () => {
    const newTest = {
      name: `Test Case ${testInputs.length + 1}`,
      input: workflowType === 'automatic' ? "" : {},
      expectedOutput: null,
      description: ""
    };
    setTestInputs([...testInputs, newTest]);
  };

  const removeTestCase = (index) => {
    const updated = testInputs.filter((_, i) => i !== index);
    setTestInputs(updated);
  };

  const updateTestCase = (index, field, value) => {
    const updated = [...testInputs];
    updated[index] = { ...updated[index], [field]: value };
    setTestInputs(updated);
  };

  const runSingleTest = async (testCase, index) => {
    const startTime = Date.now();
    
    try {
      setRealTimeResults(prev => [...prev, {
        index,
        name: testCase.name,
        status: 'running',
        startTime,
        message: 'Executing...'
      }]);

      // Build input - include file reference if selected
      let inputValue = testCase.input;
      if (testCase.inputFilePath) {
        // If a file is selected, include it in the input
        if (typeof inputValue === 'string') {
          inputValue = `${inputValue}\n\nFile: ${testCase.inputFilePath}`;
        } else if (typeof inputValue === 'object') {
          inputValue = { ...inputValue, input_file: testCase.inputFilePath };
        }
      }

      // Collect file paths: support single or multiple files
      const filePaths = testCase.inputFilePaths || (testCase.inputFilePath ? [testCase.inputFilePath] : []);

      const requestData = {
        workflow_type: workflowType,
        test_input: inputValue,
        input_file_path: filePaths[0] || null,
        input_file_paths: filePaths.length > 0 ? filePaths : undefined,
        workflow_config: {
          name: workflow.name || 'test_workflow',
          nodes: workflow.steps?.map(step => ({
            id: step.id || step.agent?.name,
            agent: {
              name: step.agent?.name,
              displayName: step.agent?.display_name || step.agent?.name,
              description: step.agent?.description || '',
              agentType: step.agent?.agentType || 'unknown',
              inputs: step.agent?.inputs || [],
              outputs: step.agent?.outputs || []
            },
            ...(step.input_from ? { input_from: step.input_from } : {}),
            ...(step.step_prompt ? { step_prompt: step.step_prompt } : {}),
          })) || [],
          connections: workflow.steps?.map((step, stepIndex) => {
            if (stepIndex < workflow.steps.length - 1) {
              return {
                source: step.agent?.name,
                target: workflow.steps[stepIndex + 1].agent?.name
              };
            }
            return null;
          }).filter(conn => conn !== null) || []
        },
        test_config: testConfiguration
      };

      const response = await axios.post(`${API_URL}/api/execute-workflow-test`, requestData, {
        timeout: testConfiguration.timeout * 1000
      });

      return {
        success: response.data.success,
        output: response.data.result,
        executionTime: response.data.execution_time,
        error: response.data.error || null,
        generated_files: response.data.generated_files || []
      };

    } catch (error) {
      const endTime = Date.now();
      const executionTime = endTime - startTime;

      return {
        success: false,
        output: null,
        executionTime,
        error: error.response?.data?.error || error.message
      };
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    setRealTimeResults([]);
    setCurrentTestIndex(0);
    
    const stats = {
      total: testInputs.length,
      passed: 0,
      failed: 0,
      running: 0,
      avgExecutionTime: 0
    };

    let totalExecutionTime = 0;

    for (let i = 0; i < testInputs.length; i++) {
      if (!isRunning) break; // Allow stopping

      setCurrentTestIndex(i);
      stats.running = 1;
      setOverallStats({ ...stats });

      const testCase = testInputs[i];
      const result = await runSingleTest(testCase, i);
      
      totalExecutionTime += result.executionTime;
      stats.avgExecutionTime = totalExecutionTime / (i + 1);

      const testResult = {
        index: i,
        testCase,
        result,
        timestamp: new Date().toISOString()
      };

      if (result.success) {
        stats.passed++;
      } else {
        stats.failed++;
      }
      stats.running = 0;

      setTestResults(prev => [...prev, testResult]);
      setRealTimeResults(prev => 
        prev.map(r => 
          r.index === i 
            ? { 
                ...r, 
                status: result.success ? 'passed' : 'failed',
                endTime: Date.now(),
                executionTime: result.executionTime,
                message: result.error || 'Completed',
                output: result.output
              }
            : r
        )
      );
      
      setOverallStats({ ...stats });

      // Add delay between tests if configured
      if (i < testInputs.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setIsRunning(false);
    setCurrentTestIndex(-1);
  };

  const stopTests = () => {
    setIsRunning(false);
  };

  const exportResults = () => {
    const exportData = {
      workflow: {
        name: workflow.name || 'unnamed_workflow',
        type: workflowType,
        timestamp: new Date().toISOString()
      },
      configuration: testConfiguration,
      tests: testResults,
      statistics: overallStats
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow_test_results_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <Spinner animation="border" size="sm" />;
      case 'passed':
        return <FaCheckCircle className="text-success" />;
      case 'failed':
        return <FaExclamationTriangle className="text-danger" />;
      default:
        return <FaClock className="text-muted" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'running':
        return 'info';
      case 'passed':
        return 'success';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Helper to extract readable text output from various result formats
  const extractTextOutput = (output) => {
    if (!output) return 'No output';
    
    // If it's a string, return directly
    if (typeof output === 'string') return output;
    
    // Try to extract text from common result formats
    if (typeof output === 'object') {
      // Check for messages array (LangGraph format)
      if (output.messages && Array.isArray(output.messages) && output.messages.length > 0) {
        const lastMessage = output.messages[output.messages.length - 1];
        if (typeof lastMessage === 'string') return lastMessage;
        if (lastMessage.content) return lastMessage.content;
        if (lastMessage.text) return lastMessage.text;
      }
      
      // Check for direct content/result/output fields
      if (output.content) return typeof output.content === 'string' ? output.content : JSON.stringify(output.content, null, 2);
      if (output.result) return typeof output.result === 'string' ? output.result : JSON.stringify(output.result, null, 2);
      if (output.output) return typeof output.output === 'string' ? output.output : JSON.stringify(output.output, null, 2);
      if (output.text) return output.text;
      if (output.answer) return output.answer;
      if (output.response) return typeof output.response === 'string' ? output.response : JSON.stringify(output.response, null, 2);
      
      // Check for final_answer (supervisor format)
      if (output.final_answer) return output.final_answer;
      
      // Check for status + message combo
      if (output.status && output.message) return `${output.status}: ${output.message}`;
      
      // Fallback: stringify the object
      return JSON.stringify(output, null, 2);
    }
    
    return String(output);
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaPlay className="me-2" />
          Workflow Tester - {workflowType === 'automatic' ? 'Automatic' : 'Manual'} Mode
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Row>
          {/* Test Configuration */}
          <Col md={4}>
            <Card className="mb-3">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h6>Test Configuration</h6>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                  >
                    {showAdvanced ? 'Basic' : 'Advanced'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    value={testConfiguration.timeout}
                    onChange={(e) => setTestConfiguration(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
                    min={10}
                    max={300}
                  />
                </Form.Group>

                {showAdvanced && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Retries</Form.Label>
                      <Form.Control
                        type="number"
                        value={testConfiguration.retries}
                        onChange={(e) => setTestConfiguration(prev => ({ ...prev, retries: parseInt(e.target.value) }))}
                        min={0}
                        max={5}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Log Level</Form.Label>
                      <Form.Select
                        value={testConfiguration.logLevel}
                        onChange={(e) => setTestConfiguration(prev => ({ ...prev, logLevel: e.target.value }))}
                      >
                        <option value="DEBUG">Debug</option>
                        <option value="INFO">Info</option>
                        <option value="WARNING">Warning</option>
                        <option value="ERROR">Error</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Check
                      type="checkbox"
                      label="Validate Outputs"
                      checked={testConfiguration.validateOutputs}
                      onChange={(e) => setTestConfiguration(prev => ({ ...prev, validateOutputs: e.target.checked }))}
                    />
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Test Statistics */}
            <Card className="mb-3">
              <Card.Header>
                <h6>Test Statistics</h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Total Tests:</span>
                    <Badge bg="primary">{overallStats.total}</Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Passed:</span>
                    <Badge bg="success">{overallStats.passed}</Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Failed:</span>
                    <Badge bg="danger">{overallStats.failed}</Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Running:</span>
                    <Badge bg="info">{overallStats.running}</Badge>
                  </div>
                </div>

                {overallStats.total > 0 && (
                  <ProgressBar>
                    <ProgressBar 
                      variant="success" 
                      now={(overallStats.passed / overallStats.total) * 100} 
                      key={1} 
                    />
                    <ProgressBar 
                      variant="danger" 
                      now={(overallStats.failed / overallStats.total) * 100} 
                      key={2} 
                    />
                  </ProgressBar>
                )}

                {overallStats.avgExecutionTime > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Avg Execution Time: {Math.round(overallStats.avgExecutionTime)}ms
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Test Cases */}
          <Col md={8}>
            <Card className="mb-3">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h6>Test Cases ({testInputs.length})</h6>
                  <div>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={addTestCase}
                      className="me-2"
                    >
                      Add Test
                    </Button>
                    <Button 
                      variant={isRunning ? "outline-danger" : "primary"}
                      size="sm"
                      onClick={isRunning ? stopTests : runAllTests}
                      disabled={testInputs.length === 0}
                    >
                      {isRunning ? (
                        <>
                          <FaStop className="me-1" />
                          Stop Tests
                        </>
                      ) : (
                        <>
                          <FaPlay className="me-1" />
                          Run All Tests
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {testInputs.map((testCase, index) => (
                  <div key={index} className="mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        {getStatusIcon(realTimeResults.find(r => r.index === index)?.status)}
                        <strong className="ms-2">
                          {testCase.name}
                          {currentTestIndex === index && isRunning && (
                            <Badge bg="info" className="ms-2">Running</Badge>
                          )}
                        </strong>
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeTestCase(index)}
                        disabled={isRunning}
                      >
                        Remove
                      </Button>
                    </div>

                    <Form.Group className="mb-2">
                      <Form.Label>Test Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={testCase.name}
                        onChange={(e) => updateTestCase(index, 'name', e.target.value)}
                        disabled={isRunning}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={testCase.description}
                        onChange={(e) => updateTestCase(index, 'description', e.target.value)}
                        disabled={isRunning}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label><strong>Text Input / Prompt</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter test input or prompt..."
                        value={typeof testCase.input === 'string' ? testCase.input : JSON.stringify(testCase.input, null, 2)}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (workflowType === 'manual') {
                            try {
                              value = JSON.parse(value);
                            } catch {
                              // Keep as string if not valid JSON
                            }
                          }
                          updateTestCase(index, 'input', value);
                        }}
                        disabled={isRunning}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label><strong>Input Files (Optional)</strong></Form.Label>
                      <div className="p-2 bg-light rounded">
                        {/* Direct file upload */}
                        <div className="mb-2">
                          <Form.Control
                            type="file"
                            multiple
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length === 0) return;
                              setTestFileUploads(prev => ({ ...prev, [index]: { uploading: true } }));
                              try {
                                for (const file of files) {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  formData.append('description', `Test input for ${testCase.name}`);
                                  const response = await axios.post(`${API_URL}/api/workflow-files/upload`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  if (response.data.success) {
                                    const fp = response.data.file.file_path;
                                    const fn = response.data.file.filename;
                                    const updated = [...testInputs];
                                    const prev = updated[index].inputFilePaths || [];
                                    const prevNames = updated[index].inputFileNames || [];
                                    updated[index] = {
                                      ...updated[index],
                                      inputFilePaths: [...prev, fp],
                                      inputFileNames: [...prevNames, fn],
                                      inputFilePath: prev.length === 0 ? fp : updated[index].inputFilePath,
                                      inputFileName: prev.length === 0 ? fn : updated[index].inputFileName,
                                    };
                                    setTestInputs(updated);
                                  }
                                }
                                await loadAvailableFiles();
                              } catch (error) {
                                alert(`Failed to upload: ${error.response?.data?.error || error.message}`);
                              } finally {
                                setTestFileUploads(prev => ({ ...prev, [index]: { uploading: false } }));
                                e.target.value = '';
                              }
                            }}
                            disabled={isRunning || testFileUploads[index]?.uploading}
                            size="sm"
                          />
                          {testFileUploads[index]?.uploading && (
                            <Spinner animation="border" size="sm" className="ms-2" />
                          )}
                        </div>
                        
                        {/* Or select from existing files */}
                        <Form.Label className="small text-muted">Or add existing file:</Form.Label>
                        <Form.Select
                          value=""
                          onChange={(e) => {
                            const fileId = e.target.value;
                            if (!fileId) return;
                            const file = availableFiles.find(f => f.id === parseInt(fileId));
                            if (!file) return;
                            const updated = [...testInputs];
                            const prev = updated[index].inputFilePaths || [];
                            const prevNames = updated[index].inputFileNames || [];
                            if (!prev.includes(file.file_path)) {
                              updated[index] = {
                                ...updated[index],
                                inputFilePaths: [...prev, file.file_path],
                                inputFileNames: [...prevNames, file.filename],
                                inputFilePath: prev.length === 0 ? file.file_path : updated[index].inputFilePath,
                                inputFileName: prev.length === 0 ? file.filename : updated[index].inputFileName,
                              };
                              setTestInputs(updated);
                            }
                          }}
                          disabled={isRunning}
                          size="sm"
                        >
                          <option value="">-- Add a file --</option>
                          {availableFiles.map(file => (
                            <option key={file.id} value={file.id}>
                              {file.filename} ({file.file_type})
                            </option>
                          ))}
                        </Form.Select>
                        
                        {/* Show selected files */}
                        {(testCase.inputFileNames || []).length > 0 && (
                          <div className="mt-2" style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                            {testCase.inputFileNames.map((fn, fi) => (
                              <Badge key={fi} bg="info" style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                                📎 {fn}
                                <span
                                  style={{cursor: 'pointer', marginLeft: 4, fontWeight: 'bold'}}
                                  onClick={() => {
                                    if (isRunning) return;
                                    const updated = [...testInputs];
                                    const fps = [...(updated[index].inputFilePaths || [])];
                                    const fns = [...(updated[index].inputFileNames || [])];
                                    fps.splice(fi, 1);
                                    fns.splice(fi, 1);
                                    updated[index] = {
                                      ...updated[index],
                                      inputFilePaths: fps,
                                      inputFileNames: fns,
                                      inputFilePath: fps[0] || null,
                                      inputFileName: fns[0] || null,
                                    };
                                    setTestInputs(updated);
                                  }}
                                >
                                  ✕
                                </span>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Group>

                    {/* Real-time results for this test */}
                    {realTimeResults.find(r => r.index === index) && (
                      <Alert variant={getStatusVariant(realTimeResults.find(r => r.index === index).status)}>
                        <div className="d-flex justify-content-between">
                          <span>{realTimeResults.find(r => r.index === index).message}</span>
                          {realTimeResults.find(r => r.index === index).executionTime && (
                            <small>{realTimeResults.find(r => r.index === index).executionTime}ms</small>
                          )}
                        </div>
                      </Alert>
                    )}
                  </div>
                ))}

                {testInputs.length === 0 && (
                  <div className="text-center text-muted">
                    <p>No test cases defined.</p>
                    <Button variant="outline-primary" onClick={addTestCase}>
                      Add First Test Case
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Test Results */}
            {testResults.length > 0 && (
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Test Results</h6>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={exportResults}
                    >
                      <FaDownload className="me-1" />
                      Export Results
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {testResults.map((result, index) => (
                    <div key={index} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          {getStatusIcon(result.result.success ? 'passed' : 'failed')}
                          <span className="ms-2">
                            <strong>{result.testCase.name}</strong>
                            {result.result.executionTime && (
                              <small className="text-muted ms-2">({result.result.executionTime}ms)</small>
                            )}
                          </span>
                        </div>
                        <Badge bg={result.result.success ? 'success' : 'danger'}>
                          {result.result.success ? 'PASS' : 'FAIL'}
                        </Badge>
                      </div>
                      
                      {result.result.error && (
                        <Alert variant="danger" className="py-2 mb-2">
                          <small><strong>Error:</strong> {result.result.error}</small>
                        </Alert>
                      )}
                      
                      {/* Text Output - Always show prominently */}
                      {result.result.output && (
                        <div className="mb-2">
                          <strong className="small text-muted">📝 Output:</strong>
                          <div 
                            className="mt-1 p-2 bg-light border rounded" 
                            style={{ 
                              maxHeight: '150px', 
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontSize: '0.85rem'
                            }}
                          >
                            {extractTextOutput(result.result.output)}
                          </div>
                        </div>
                      )}

                      {/* Generated Files */}
                      {result.result.output?.generated_files && result.result.output.generated_files.length > 0 && (
                        <div className="mb-2">
                          <strong className="small text-muted">📁 Generated Files:</strong>
                          <ul className="mb-0 mt-1 small">
                            {result.result.output.generated_files.map((file, idx) => (
                              <li key={idx}><code>{typeof file === 'string' ? file : file.filename || JSON.stringify(file)}</code></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Raw Output (collapsed) */}
                      {result.result.output && typeof result.result.output === 'object' && (
                        <details className="mt-2">
                          <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                            View Raw Result
                          </summary>
                          <pre className="mt-1 p-2 bg-dark text-light rounded" style={{ fontSize: '0.75rem', maxHeight: '100px', overflow: 'auto' }}>
                            {JSON.stringify(result.result.output, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {testResults.length > 0 && (
          <Button variant="outline-primary" onClick={exportResults}>
            <FaDownload className="me-1" />
            Export Results
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WorkflowTester;
